import { useMemo } from "react";
import type { CalendarPickerLocation } from "@dentacore/shared-types";

import { atLocation } from "../api/client";
import { locate } from "../api/located";
import { useActiveBranch } from "../api/use-active-branch";
import { useCalendarLocations } from "../calendar/calendar-selection";
import { LocationBadge } from "../calendar/location-badge";
import { type Located } from "./group-by-name";
import styles from "./locations.module.css";

export { groupByName, nameKey, type Grouped, type Located } from "./group-by-name";

/**
 * **Several locations on every screen** (Emile, 2026-10-22).
 *
 * The locations ticked in the top bar's picker — the Calendar's choice (CF-D12) —
 * now apply to every location screen. One location ticked, and nothing changes.
 */
export interface Shown {
  ids: readonly string[];
  /** More than one location on screen. */
  many: boolean;
  byId: ReadonlyMap<string, CalendarPickerLocation>;
  /** Changes when the choice does, for effects to reload on. */
  key: string;
  active: string | null;
}

export function useShown(): Shown {
  const { shown, reachable } = useCalendarLocations();
  const active = useActiveBranch();
  const key = shown.join(",");
  return useMemo(
    () => ({
      ids: shown,
      many: shown.length > 1,
      byId: new Map(reachable.map((location) => [location.id, location])),
      key,
      active,
    }),
    // `shown` is rebuilt every render; `key` is what it says.
    [key, reachable, active],
  );
}

/**
 * One list per location, read with that location's header and joined. Every row
 * carries `at`, and its id is remembered so a change to it goes to its location.
 */
export async function across<T extends { id: string }>(
  ids: readonly string[],
  call: () => Promise<readonly T[]>,
): Promise<Located<T>[]> {
  const lists = await Promise.all(ids.map((id) => atLocation(id, call)));
  return lists.flatMap((list, index) => {
    const at = ids[index] as string;
    return list.map((row) => {
      locate(row.id, at);
      return { ...row, at };
    });
  });
}

/**
 * `across` for rows that have no id of their own; `remember` registers the ids
 * inside a row (its offers, its stock item) with the row's location.
 */
export async function acrossRows<T>(
  ids: readonly string[],
  call: () => Promise<readonly T[]>,
  remember?: (row: T, at: string) => void,
): Promise<Located<T>[]> {
  const lists = await Promise.all(ids.map((id) => atLocation(id, call)));
  return lists.flatMap((list, index) => {
    const at = ids[index] as string;
    return list.map((row) => {
      remember?.(row, at);
      return { ...row, at };
    });
  });
}

/** One answer per location, for figures that are added up rather than listed. */
export function eachLocation<T>(ids: readonly string[], call: () => Promise<T>): Promise<T[]> {
  return Promise.all(ids.map((id) => atLocation(id, call)));
}

/** The location codes a row exists at, in the picker's order. */
export function Badges({
  ats,
  shown,
  small = true,
}: {
  ats: readonly string[];
  shown: Shown;
  small?: boolean;
}): React.JSX.Element | null {
  if (!shown.many) return null;
  return (
    <span className={styles.badges}>
      {shown.ids
        .filter((id) => ats.includes(id))
        .map((id) => {
          const location = shown.byId.get(id);
          if (location === undefined) return null;
          return (
            <LocationBadge
              key={id}
              code={location.code}
              hue={location.hue}
              name={location.name}
              size={small ? "small" : "normal"}
            />
          );
        })}
    </span>
  );
}

/** The name of a location, for a sentence. */
export function locationName(shown: Shown, id: string): string {
  return shown.byId.get(id)?.name ?? "this location";
}

/**
 * **Where a new record goes** (the decision of 2026-10-22: ask in the form). Drawn
 * only while several locations are on screen; it defaults to the active one.
 */
export function LocationField({
  shown,
  value,
  onChange,
}: {
  shown: Shown;
  value: string | null;
  onChange: (id: string) => void;
}): React.JSX.Element | null {
  if (!shown.many) return null;
  return (
    <label className={styles.field}>
      <span>Location</span>
      <select
        value={value ?? shown.active ?? ""}
        onChange={(event) => onChange(event.target.value)}
        data-testid="location-field"
      >
        {shown.ids.map((id) => (
          <option key={id} value={id}>
            {locationName(shown, id)}
          </option>
        ))}
      </select>
    </label>
  );
}

/** A switch between a record's copies, one per location (drawn only for two or more). */
export function CopySwitch<T extends { id: string }>({
  copies,
  current,
  shown,
  onPick,
}: {
  copies: readonly Located<T>[];
  current: string | null;
  shown: Shown;
  onPick: (id: string) => void;
}): React.JSX.Element | null {
  if (copies.length < 2) return null;
  return (
    <div className={styles.switch} role="tablist" aria-label="Location">
      {copies.map((copy) => {
        const location = shown.byId.get(copy.at);
        return (
          <button
            key={copy.id}
            type="button"
            role="tab"
            aria-selected={copy.id === current}
            onClick={() => onPick(copy.id)}
          >
            {location !== undefined && (
              <LocationBadge
                code={location.code}
                hue={location.hue}
                name={location.name}
                size="small"
              />
            )}
            {location?.name ?? "Location"}
          </button>
        );
      })}
    </div>
  );
}
